


































import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { MiraInsurer } from "@/data/models/MiraInsurerAdministration";
import { mergeMiraInsurer } from "@/data/repository/MiraInsurerAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { insurerErrorMessage } from "@/utils/MiraInsurerAdministration";

/** What `show()` resolves with: merged, refused as changed meanwhile (the caller reloads), or closed. */
export type MiraInsurerMergeResult = "MERGED" | "RELOAD" | null;

/**
 * Zusammenführen (#2610125, part 2): one insurer goes into another. The target takes over everything the
 * source is recognised by (names, keys, spellings, words), the source is retired and points at the target
 * (`mergeMiraInsurerForAdministration`). The warning names what it reaches, so there is no second question.
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class MiraInsurerMergeDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  busy = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  source: MiraInsurer | null = null;
  insurers: MiraInsurer[] = [];
  targetKey: string | null = null;

  private resolve: ((result: MiraInsurerMergeResult) => void) | null = null;

  /** The other active insurers, by short name. */
  get insurerItems(): Array<{ key: string; label: string }> {
    return this.insurers
      .filter((insurer) => !insurer.retired && insurer.key !== this.source?.key)
      .map((insurer) => ({ key: insurer.key, label: `${insurer.shortName} (${insurer.code})` }))
      .sort((a, b) => a.label.localeCompare(b.label, "de"));
  }

  get target(): MiraInsurer | null {
    const key = this.targetKey;
    return this.insurers.find((insurer) => insurer.key === key) ?? null;
  }

  get introText(): string {
    return this.source ? this.$t("admin.miraInsurers.merge.intro", [this.source.shortName, this.source.code]).toString() : "";
  }

  get effectText(): string | null {
    const target = this.target;
    return this.source && target ? this.$t("admin.miraInsurers.merge.effect", [this.source.shortName, target.shortName]).toString() : null;
  }

  /**
   * @param source - the insurer that goes into another.
   * @param insurers - the whole list, for the targets.
   */
  show(source: MiraInsurer, insurers: MiraInsurer[]): Promise<MiraInsurerMergeResult> {
    this.source = source;
    this.insurers = insurers;
    this.targetKey = null;
    this.baseDialog.show({
      title: this.$t("admin.miraInsurers.merge.title").toString(),
      positiveButtonText: this.$t("admin.miraInsurers.merge.action").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  async onMerge() {
    const source = this.source;
    const target = this.target;
    if (!source || !target) return;
    this.busy = true;
    try {
      await mergeMiraInsurer({
        key: source.key,
        expectedUpdatedAt: source.updatedAt,
        into: target.key,
        intoExpectedUpdatedAt: target.updatedAt,
      });
      SnackbarModule.showSuccess({ key: "admin.miraInsurers.merge.done" });
      this.close("MERGED");
    } catch (error) {
      const message = insurerErrorMessage(error);
      SnackbarModule.showError(message);
      // Either insurer changed since the list loaded: close, so the list shows what is there now.
      if (message.key.endsWith("CHANGED_MEANWHILE")) this.close("RELOAD");
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraInsurerMergeResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  finish(result: MiraInsurerMergeResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
